import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/system';
import { debounce } from 'lodash';
import { isMobile } from 'react-device-detect';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { t } from '../../../i18n';
import { ISitOutBankState } from '../../../Core/State/IPlayerState';
import barsIcon from '/images/table/bars.svg';
import { playAudio } from '../../../Utils/AudioUtils';

interface TurnActionControlsProps {
    gameType: string;
    isSitOut: boolean;
    isTournamentSitOut?: boolean;
    sitOutBank?: ISitOutBankState | null;
    isWaitingForBigBlind?: boolean;
    isCurrentPlayerTurn: boolean;
    availableActions?: {
        actions: Array<{
            type: string;
            minAmount: number;
            maxAmount: number;
        }>;
    };
    betAmount: number;
    smallBlind: number;
    onRejoinClick: () => void;
    onBetAmountChange: (newValue: number | number[] | React.ChangeEvent<HTMLInputElement> | string) => void;
    onSendAction: (actionType: string, amount: number) => void;
    formatCurrency: (num: number) => string;
    currencyType?: 'fiat' | 'crypto' | 'chips' | 'tournament_chips';
    isAutoAction?: boolean;
    selectedAutoAction?: string | null;
    preActionFoldLabel?: string;
}

const CustomSlider = styled(Slider)(({}) => ({
    color: '#303136',
    margin: '0 10px',
    width: 'initial',
    flexGrow: 3,
    '& .MuiSlider-thumb': {
        backgroundColor: '#3DE361',
        border: 'none',
        borderRadius: '4px',
        padding: '0',
        width: 'clamp(18px, 2vw, 23px)',
        height: 'clamp(18px, 2vw, 23px)',
        zIndex: 2,
        boxShadow: `0px 7px #125234`,
        backgroundImage: `url(${barsIcon})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
    },
    '& .MuiSlider-rail': {
        color: '#52525D',
        opacity: 1,
    },
    '& .MuiSlider-track': {
        height: '3px',
        color: 'transparent',
        backgroundColor: '#52525D',
        zIndex: 1,
        opacity: 1,
    },
    '& .MuiSlider-mark': {
        width: '2px',
        height: '10px',
        backgroundColor: '#52525D',
        borderRadius: '4px',
        zIndex: 1
    },
}));

const darkTheme = createTheme({
    palette: {
        mode: "dark",
        success: {
            main: "#3ad65d",
        },
        error: {
            main: "#f52740",
        },
        secondary: {
            main: "#cc976d",
        },
        info: {
            main: "#3e414d",
        },
    }
});

const getAuthoritativeSitOutBankSeconds = (sitOutBank?: ISitOutBankState | null): number | null => {
    if (!sitOutBank?.isActive || !Number.isFinite(sitOutBank.remainingSeconds)) return null;
    return Math.max(0, Math.ceil(sitOutBank.remainingSeconds));
};

const getLiveSitOutBankSeconds = (sitOutBank?: ISitOutBankState | null): number | null => {
    if (!sitOutBank?.isActive) return null;
    if (sitOutBank.expiresAt) {
        const expiresAtMs = Date.parse(sitOutBank.expiresAt);
        if (Number.isFinite(expiresAtMs)) {
            return Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));
        }
    }
    return getAuthoritativeSitOutBankSeconds(sitOutBank);
};

const formatCountdown = (seconds: number): string => {
    const safeSeconds = Math.max(0, Math.ceil(seconds));
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const TurnActionControls: React.FC<TurnActionControlsProps> = ({
    gameType,
    isSitOut,
    isTournamentSitOut = false,
    sitOutBank = null,
    isWaitingForBigBlind = false,
    isCurrentPlayerTurn,
    availableActions,
    betAmount,
    smallBlind,
    onRejoinClick,
    onBetAmountChange,
    onSendAction,
    formatCurrency,
    currencyType,
    isAutoAction = false,
    selectedAutoAction = null,
    preActionFoldLabel
}) => {
    const [tempInputValue, setTempInputValue] = React.useState('');
    const [isRaiseExpanded, setIsRaiseExpanded] = useState<boolean>(false);
    const [sitOutBankRemainingSeconds, setSitOutBankRemainingSeconds] = React.useState<number | null>(
        () => getAuthoritativeSitOutBankSeconds(sitOutBank)
    );

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (isMobile && !isRaiseExpanded) {
            e.preventDefault();
            setIsRaiseExpanded(true);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const raiseOrBetAction = availableActions?.actions?.find(a =>
            a.type === 'raise' || a.type === 'bet'
        );
        
        if (!raiseOrBetAction || !value) {
            setTempInputValue('');
            return;
        }

        const regex = (currencyType === 'chips' || currencyType === 'tournament_chips')
            ? /^\d*$/
            : /^\d*\.?\d{0,2}$/;

        if (regex.test(value)) {
            setTempInputValue(value);
            
            if (value !== '' && value !== '.') {
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                    const roundedValue = (currencyType === 'chips' || currencyType === 'tournament_chips') ? Math.floor(numValue) : numValue;
                    const boundedValue = Math.max(
                        raiseOrBetAction.minAmount,
                        Math.min(raiseOrBetAction.maxAmount, roundedValue)
                    );
                    onBetAmountChange(boundedValue);
                }
            }
        }
    };

    const handleInputBlur = () => {
        const raiseOrBetAction = availableActions?.actions?.find(a =>
            a.type === 'raise' || a.type === 'bet'
        );
        
        if (!raiseOrBetAction) return;

        setTempInputValue('');
        onBetAmountChange(Math.max(
            raiseOrBetAction.minAmount,
            Math.min(raiseOrBetAction.maxAmount, betAmount)
        ));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const raiseOrBetAction = availableActions?.actions?.find(a =>
                a.type === 'raise' || a.type === 'bet'
            );
            if (raiseOrBetAction) {
                onSendAction(raiseOrBetAction.type, betAmount);
            }
        }
    };

    const [betSliderValue, setBetSliderValue] = useState<number>(
        betAmount
    );
    const shouldShowSitOutBankCountdown = isSitOut
        && !isTournamentSitOut
        && sitOutBank?.isActive === true
        && sitOutBankRemainingSeconds !== null;
    const sitOutMessage = isTournamentSitOut
        ? 'Sitting out. Hands auto-check/fold. Blinds and antes continue.'
        : shouldShowSitOutBankCountdown
            ? `Sitting out. Sit back in before your seat expires in ${formatCountdown(sitOutBankRemainingSeconds)}.`
            : 'Sitting out. Sit back in before your seat expires.';
    const statusMessage = (isSitOut || isTournamentSitOut)
        ? sitOutMessage
        : 'Waiting for big blind.';
    const shouldShowStatusPanel = isSitOut || isTournamentSitOut || isWaitingForBigBlind;

    // When betAmount changes, update the slider value
    React.useEffect(() => {
        setBetSliderValue(betAmount);
    }, [betAmount]);

    React.useEffect(() => {
        setSitOutBankRemainingSeconds(getAuthoritativeSitOutBankSeconds(sitOutBank));
        if (!sitOutBank?.isActive || !sitOutBank.expiresAt) return;

        const interval = setInterval(() => {
            setSitOutBankRemainingSeconds(getLiveSitOutBankSeconds(sitOutBank));
        }, 1000);

        return () => clearInterval(interval);
    }, [sitOutBank]);

    const getActionButtonColor = (type: string, isActionAvailable: boolean, isAutoAction: boolean, isAutoActionSelected: boolean) => {
        if (!isActionAvailable || (isAutoAction && !isAutoActionSelected)) {
            return '#262934';
        }
        switch (type) {
            case 'call':
            case 'call-any':
                return '#71411B';
            case 'check':
                return '#262934';
            case 'bet':
            case 'raise':
                return '#0B452A';
            case 'all-in':
                return '#704406';
            case 'fold':
                return '#7E121F';
            default:
                return '#262934';
        }
    };

    const getActionBgColor = (type: string, isActionAvailable: boolean, isAutoAction: boolean, isAutoActionSelected: boolean, isHover: boolean) => {
        if (!isActionAvailable || (isAutoAction && !isAutoActionSelected && !isHover)) {
            return '#777A84';
        }
        switch (type) {
            case 'call':
            case 'call-any':
                return '#CC976D';
            case 'check':
                return '#777A84';
            case 'bet':
            case 'raise':
                return '#3AD65D';
            case 'all-in':
                return '#E5C353';
            case 'fold':
                return '#F52740';
            default:
                return '#777A84';
        }
    }

    const getActionShadowColor = (type: string, isActionAvailable: boolean, isAutoAction: boolean, isAutoActionSelected: boolean, isHover: boolean) => {
        if (!isActionAvailable || (isAutoAction && !isAutoActionSelected && !isHover)) {
            return '#41444C';
        }
        switch (type) {
            case 'call':
            case 'call-any':
                return '#7E4D26';
            case 'check':
                return '#41444C';
            case 'bet':
            case 'raise':
                return '#125234';
            case 'all-in':
                return '#8B5A00';
            case 'fold':
                return '#941121';
            default:
                return '#41444C';
        }
    }

    const getActionGradient = (type: string, isActionAvailable: boolean, isAutoAction: boolean, isAutoActionSelected: boolean) => {
        if (!isActionAvailable || (isAutoAction && !isAutoActionSelected)) {
            return 'linear-gradient(273deg, rgba(159, 163, 177, 0.27) 60.82%, rgba(95, 98, 107, 0.40) 80.17%, rgba(95, 98, 107, 0.40) 99.52%)';
        }
        switch (type) {
            case 'call':
            case 'call-any':
                return 'linear-gradient(273deg, rgba(255, 224, 173, 0.16) 60.82%, rgba(204, 151, 109, 0.40) 80.17%, rgba(204, 151, 109, 0.40) 99.52%)';
            case 'check':
                return 'linear-gradient(273deg, rgba(159, 163, 177, 0.27) 60.82%, rgba(95, 98, 107, 0.40) 80.17%, rgba(95, 98, 107, 0.40) 99.52%)';
            case 'bet':
            case 'raise':
                return 'linear-gradient(273deg, rgba(154, 255, 177, 0.27) 60.82%, rgba(58, 214, 93, 0.40) 80.17%, rgba(58, 214, 93, 0.40) 99.52%)';
            case 'all-in':
                return 'linear-gradient(273deg, rgba(255, 238, 190, 0.16) 60.82%, rgba(229, 195, 83, 0.40) 80.17%, rgba(229, 195, 83, 0.40) 99.52%)';
            case 'fold':
                return 'linear-gradient(273deg, rgba(255, 144, 97, 0.27) 60.82%, rgba(245, 39, 64, 0.40) 80.17%, rgba(245, 39, 64, 0.40) 99.52%)';
            default:
                return 'linear-gradient(273deg, rgba(159, 163, 177, 0.27) 60.82%, rgba(95, 98, 107, 0.40) 80.17%, rgba(95, 98, 107, 0.40) 99.52%)';
        }
    }

    const getActionHoverGradient = (type: string, isActionAvailable: boolean, isAutoAction: boolean, isAutoActionSelected: boolean) => {
        if (!isActionAvailable) {
            return 'linear-gradient(273deg, rgba(220, 227, 246, 0.61) 60.82%, rgba(119, 122, 132, 0.90) 80.17%, rgba(119, 122, 132, 0.90) 99.52%)';
        }
        switch (type) {
            case 'call':
            case 'call-any':
                return 'linear-gradient(273deg, rgba(255, 224, 173, 0.81) 60.82%, rgba(204, 151, 109, 0.90) 80.17%, rgba(204, 151, 109, 0.90) 99.52%)';
            case 'check':
                return 'linear-gradient(273deg, rgba(220, 227, 246, 0.61) 60.82%, rgba(119, 122, 132, 0.90) 80.17%, rgba(119, 122, 132, 0.90) 99.52%)';
            case 'bet':
            case 'raise':
                return 'linear-gradient(273deg, rgba(154, 255, 177, 0.90) 60.82%, rgba(58, 214, 93, 0.90) 80.17%, rgba(58, 214, 93, 0.90) 99.52%)';
            case 'all-in':
                return 'linear-gradient(273deg, rgba(255, 238, 190, 0.81) 60.82%, rgba(229, 195, 83, 0.90) 80.17%, rgba(229, 195, 83, 0.90) 99.52%)';
            case 'fold':
                return 'linear-gradient(273deg, rgba(255, 119, 114, 0.90) 60.82%, rgba(245, 39, 64, 0.90) 80.17%, rgba(245, 39, 64, 0.90) 99.52%)';
            default:
                return 'linear-gradient(273deg, rgba(220, 227, 246, 0.61) 60.82%, rgba(119, 122, 132, 0.90) 80.17%, rgba(119, 122, 132, 0.90) 99.52%)';
        }
    }

    const getActionBorderColor = (type: string, isActionAvailable: boolean, isAutoAction: boolean, isAutoActionSelected: boolean, isHover: boolean) => {
        if (!isActionAvailable || (isAutoAction && !isAutoActionSelected && !isHover)) {
            return '#A4A8B4';
        }
        switch (type) {
            case 'call':
            case 'call-any':
                return '#FFD38C';
            case 'check':
                return '#A4A8B4';
            case 'bet':
            case 'raise':
                return '#93F9AA';
            case 'all-in':
                return '#FFDE97';
            case 'fold':
                return '#FF7272';
            default:
                return '#A4A8B4';
        }
    }

    const controlBottom = isMobile ? '10px' : 'clamp(8px, 1.4vh, 16px)';
    const panelGap = '4px';
    const panelPadding = isMobile ? '10px' : 'clamp(10px, 1.2vw, 20px)';
    const actionButtonMinWidth = isMobile ? 'initial' : 'clamp(80px, 15vw, 120px)';
    const actionButtonMaxWidth = isMobile ? 'initial' : 'clamp(80px, 15vw, 135px)';
    const actionButtonHeight = isMobile ? '44px' : 'clamp(32px, 3.6vh, 42px)';
    const actionButtonFontSize = isMobile ? '17px' : 'clamp(11px, 1.3vw, 14px)';
    const actionButtonLineHeight = isMobile ? '18px' : 'clamp(12px, 1.35vw, 15px)';
    const actionAmountFontSize = isMobile ? '15px' : 'clamp(10px, 1.15vw, 13px)';
    const quickBetButtonSx = {
        backgroundColor: '#24242F',
        color: 'white',
        textTransform: 'capitalize',
        fontSize: isMobile ? '10px' : 'clamp(8px, 0.9vw, 10px)',
        fontWeight: 'bold',
        boxShadow: 'none',
        padding: isMobile ? '8px 12px' : 'clamp(3px, 0.7vh, 5px) clamp(5px, 1.2vh, 10px)',
        border: '1px solid #52525D',
        '&:hover': { backgroundColor: '#292A2F' },
        width: '10%'
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                boxSizing: 'border-box',
                position: 'fixed',
                left: isMobile ? '10px' : '50%',
                bottom: controlBottom,
                zIndex: 100000,
                width: isMobile ? 'calc(100% - 20px)' : 'initial',
                background: 'linear-gradient(122deg, #43424A 6.85%, #302F39 52.46%)',
                borderRadius: '6px',
                transition: 'all 0.3s ease-in-out',
                transform: isMobile ? 'initial' : 'translateX(-50%)'
            }}>
                {shouldShowStatusPanel ? (
                    <Box sx={{ position: 'fixed', bottom: controlBottom, left: isMobile ? '10px' : '50%', right: isMobile ? '10px' : '', transform: isMobile ? '' : 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: panelGap, backgroundColor: isMobile ? '' : 'rgba(61,62,68,0.2)', padding: panelPadding, borderRadius: '6px' }}>
                        <Box sx={{
                            color: '#FFFFFF',
                            fontFamily: '"Inter", serif',
                            fontSize: isMobile ? '12px' : 'clamp(11px, 1vw, 13px)',
                            fontWeight: 600,
                            lineHeight: 1.35,
                            textAlign: 'center',
                            maxWidth: isMobile ? '100%' : 'clamp(260px, 34vw, 360px)',
                            minWidth: '200px',
                            textShadow: '0 1px 2px rgba(0,0,0,0.45)'
                        }}>
                            {statusMessage}
                        </Box>
                        {(isSitOut || isTournamentSitOut) && (
                            <Button
                                variant="contained"
                                color="success"
                                onClick={onRejoinClick}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: "1",
                                    fontFamily: '"Inter", serif',
                                    fontSize: isMobile ? '16px' : 'clamp(13px, 1.4vw, 18px)',
                                    fontWeight: '700',
                                    textTransform: 'capitalize',
                                    padding: isMobile ? '8px 16px' : 'clamp(8px, 1vw, 10px) clamp(18px, 2.5vw, 32px)',
                                    boxShadow: 'none',
                                    minHeight: actionButtonHeight,
                                    color: "#20202c",
                                    width: isMobile ? '100%' : 'auto'
                                }}
                            >
                                {isTournamentSitOut ? t('TurnActionControls:sitBackIn') : t('TurnActionControls:rejoinGame')}
                            </Button>
                        )}
                    </Box>
                ) : (
                    (isAutoAction || isCurrentPlayerTurn) && availableActions?.actions && availableActions.actions.length > 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: panelGap,
                            maxWidth: isMobile ? '100%' : 'calc(100dvw - 120px)',
                            width: isMobile ? '100%' : 'initial',
                            padding: panelPadding,
                            alignSelf: 'flex-end'
                        }}>
                            <Stack direction="row" sx={{ display: 'flex', alignItems: 'flex-end', gap: isMobile ? '10px' : '16px', flexWrap: 'nowrap' }}>
                                {availableActions.actions.map((action, index) => {
                                    const isActionAvailable = availableActions.actions.some(a => a.type === action.type);

                                    let label = action.type.toUpperCase();
                                    let amount = formatCurrency(betAmount);

                                    if (action.type === 'bet' || action.type === 'raise') {
                                        label = t(`TurnActionControls:actions.${action.type}`);
                                    } else if (action.type === 'call') {
                                        label = t('TurnActionControls:actions.call');
                                        amount = formatCurrency(action.minAmount);
                                    } else if (action.type === 'call-any') {
                                        label = 'Call Any';
                                        amount = '';
                                    } else if (action.type === 'check') {
                                        label = t('TurnActionControls:actions.check');
                                        amount = '';
                                    } else if (action.type === 'fold') {
                                        label = t('TurnActionControls:actions.fold');
                                        amount = '';
                                    } else if (action.type === 'all-in') {
                                        label = t('TurnActionControls:betSizes.allIn');
                                        amount = formatCurrency(action.maxAmount);
                                    }

                                    // (Texas): Override fold label for pre-action: "Check/Fold" when amountToCall === 0
                                    if (isAutoAction && action.type === 'fold' && preActionFoldLabel) {
                                        label = preActionFoldLabel;
                                    }

                                    const isSelectedAutoAction = isAutoAction && selectedAutoAction === action.type.toLowerCase();
                                    const autoActionColor = getActionButtonColor(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction);
                                    const autoActionBgColor = getActionBgColor(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction, false);
                                    const autoActionHoverBgColor = getActionBgColor(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction, true);
                                    const autoActionShadowColor = getActionShadowColor(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction, false);
                                    const autoActionHoverShadowColor = getActionShadowColor(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction, true);
                                    const autoActionGradient = getActionGradient(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction);
                                    const autoActionHoverGradient = getActionHoverGradient(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction);
                                    const autoActionBorderColor = getActionBorderColor(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction, false);
                                    const autoActionHoverBorderColor = getActionBorderColor(action.type, isActionAvailable, isAutoAction, isSelectedAutoAction, true);
                                    
                                    return (
                                        <Button
                                            key={index}
                                            variant="contained"
                                            color='primary'
                                            onMouseEnter={ () => {
                                                    playAudio('action-hover.mp3');
                                                }
                                            }
                                            onClick={() => {
                                                if (isAutoAction) {
                                                    if (isSelectedAutoAction) {
                                                        playAudio('pre-action-unclick.mp3');
                                                    } else {
                                                        playAudio('pre-action-click.mp3');
                                                    }
                                                } else {
                                                    playAudio('action-click.mp3');
                                                }
                                                const amountToSend = action.type === 'all-in'
                                                    ? action.maxAmount
                                                    : action.type === 'raise' || action.type === 'bet'
                                                        ? betAmount
                                                        : action.minAmount;
                                                onSendAction(action.type, amountToSend);
                                            }}
                                            className={isSelectedAutoAction ? 'active' : ''}
                                            disabled={!isActionAvailable}
                                            sx={{ 
                                                display: "flex",
                                                flexDirection: "column",
                                                flex: "1",
                                                fontFamily: '"Inter", serif',
                                                fontSize: actionButtonFontSize,
                                                fontWeight: '700',
                                                minWidth: actionButtonMinWidth,
                                                maxWidth: actionButtonMaxWidth,
                                                textTransform: 'capitalize',
                                                padding: isMobile ? '5px 16px' : 'clamp(4px, 0.7vw, 5px) clamp(10px, 1.4vw, 16px)',
                                                lineHeight: actionButtonLineHeight,
                                                height: actionButtonHeight,
                                                backgroundColor: autoActionBgColor,
                                                boxShadow: `0px 7px ${autoActionShadowColor}`,
                                                borderRadius: '6px',
                                                marginBottom: '7px',
                                                color: autoActionColor,
                                                border: `1px solid ${autoActionBorderColor}`,
                                                transition: 'none',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '10px',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: '#23232A',
                                                    borderRadius: '6px',
                                                    zIndex: -1
                                                },
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '4px',
                                                    right: '4px',
                                                    bottom: '4px',
                                                    left: '4px',
                                                    borderRadius: '4px',
                                                    background: autoActionGradient,
                                                    transition: 'none',
                                                    zIndex: 1,
                                                },
                                                '&:hover': {
                                                    backgroundColor: autoActionHoverBgColor,
                                                    border: `1px solid ${autoActionHoverBorderColor}`,
                                                    boxShadow: `0px 7px ${autoActionHoverShadowColor}`,
                                                    '&::before': {
                                                        boxShadow: `0px 0px 8px ${autoActionHoverBgColor}`,
                                                        filter: 'blur(2px)'
                                                    },
                                                    '&::after': {
                                                        background: autoActionHoverGradient,
                                                    }
                                                },
                                                '&.active': {
                                                    backgroundColor: autoActionBgColor,
                                                    boxShadow: `0px 0px ${autoActionShadowColor}`,
                                                    marginBottom: '0px',
                                                    marginTop: '7px',
                                                    '&::before': {
                                                        top: '3px',
                                                        boxShadow: `0px 0px 8px 2px ${autoActionBgColor}`
                                                    },
                                                    '&::after': {
                                                        background: autoActionHoverGradient,
                                                    }
                                                },
                                                '&:active': {
                                                    backgroundColor: autoActionBgColor,
                                                    boxShadow: `0px 0px ${autoActionShadowColor}`,
                                                    marginBottom: '0px',
                                                    marginTop: '7px',
                                                    '&::before': {
                                                        top: '3px',
                                                        boxShadow: `0px 0px 8px 2px ${autoActionBgColor}`
                                                    },
                                                    '&::after': {
                                                        background: autoActionHoverGradient,
                                                    }
                                                }
                                            }}
                                        >
                                            <span style={{ zIndex: 2 }}>{label}</span>
                                            {amount !== '' && (
                                                <span style={{ fontSize: actionAmountFontSize, zIndex: 2 }}>{amount}</span>
                                            )}
                                        </Button>
                                    );
                                })}
                            </Stack>
                            {(() => {
                                const raiseOrBetAction = isAutoAction
                                    ? undefined
                                    : availableActions?.actions?.find(a =>
                                        a.type === 'raise' || a.type === 'bet'
                                    );
                                const allInAction = availableActions?.actions?.find(a => a.type === 'all-in');

                                if (!raiseOrBetAction) {
                                    return null;
                                }

                                return (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: panelGap,
                                        width: '100%'
                                    }}>
                                        <Stack direction={isMobile ? 'row-reverse' : 'row'} sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : 'clamp(5px, 0.8vw, 10px)', marginLeft: isMobile ? '' : 'auto', flexWrap: 'nowrap', position: 'relative', marginTop: isMobile ? '0' : '7px' }}>
                                            <Stack direction={ isMobile ? "column" : "row" } gap="8px" sx={{ position: isMobile ? 'absolute' : 'relative', bottom: isMobile ? 'calc(100% + 85px)' : 'initial', right: isMobile ? '0' : 'initial' }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => {
                                                        playAudio('pre-action-click.mp3');
                                                        onBetAmountChange('halfPot');
                                                    }}
                                                    disabled={!raiseOrBetAction}
                                                    sx={quickBetButtonSx}
                                                >
                                                    {t('TurnActionControls:betSizes.halfPot')}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => {
                                                        playAudio('pre-action-click.mp3');
                                                        onBetAmountChange('threeFourthsPot');
                                                    }}
                                                    disabled={!raiseOrBetAction}
                                                    sx={quickBetButtonSx}
                                                >
                                                    {t('TurnActionControls:betSizes.threeFourthsPot')}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => {
                                                        playAudio('pre-action-click.mp3');
                                                        onBetAmountChange('fullPot');
                                                    }}
                                                    disabled={!raiseOrBetAction}
                                                    sx={quickBetButtonSx}
                                                >
                                                    {t('TurnActionControls:betSizes.pot')}
                                                </Button>
                                            </Stack>
                                            {/* <Button
                                                variant="contained"
                                                onClick={() => allInAction && onSendAction(allInAction.type, allInAction.maxAmount)}
                                                disabled={!allInAction}
                                                sx={quickBetButtonSx}
                                            >
                                                {t('TurnActionControls:betSizes.allIn')}
                                            </Button> */}
                                            <CustomSlider
                                                value={betSliderValue}
                                                min={raiseOrBetAction.minAmount}
                                                max={raiseOrBetAction.maxAmount}
                                                step={(currencyType === 'chips' || currencyType === 'tournament_chips') ? 1 : smallBlind}
                                                marks={[
                                                    { value: raiseOrBetAction.minAmount },
                                                    { value: raiseOrBetAction.maxAmount * 0.2 },
                                                    { value: raiseOrBetAction.maxAmount * 0.4 },
                                                    { value: raiseOrBetAction.maxAmount * 0.6 },
                                                    { value: raiseOrBetAction.maxAmount * 0.8 },
                                                    { value: raiseOrBetAction.maxAmount }
                                                ]}
                                                onChange={(_, newValue) => {
                                                    if (typeof newValue === 'number' && raiseOrBetAction) {
                                                        const value = Math.max(
                                                            raiseOrBetAction.minAmount,
                                                            Math.min(raiseOrBetAction.maxAmount, Math.floor(newValue * 100) / 100)
                                                        );
                                                        onBetAmountChange(value);
                                                        setBetSliderValue(value);
                                                    }
                                                }}
                                                onChangeCommitted={debounce((_, newValue) => {
                                                    if (typeof newValue === 'number' && raiseOrBetAction) {
                                                        const value = Math.max(
                                                            raiseOrBetAction.minAmount,
                                                            Math.min(raiseOrBetAction.maxAmount, Math.floor(newValue * 100) / 100)
                                                        );
                                                        onBetAmountChange(value);
                                                        setBetSliderValue(value);
                                                    }
                                                }, 100)}
                                                valueLabelDisplay="off"
                                                disabled={!raiseOrBetAction}
                                            />
                                            <input
                                                type="text"
                                                value={tempInputValue || (betAmount ? ((currencyType === 'chips' || currencyType === 'tournament_chips') ? Math.floor(betAmount) : betAmount.toFixed(2)) : '0')}
                                                pattern={(currencyType === 'chips' || currencyType === 'tournament_chips') ? "[0-9]*" : "[0-9]+([\\.,][0-9]{1,2})?"}
                                                inputMode={(currencyType === 'chips' || currencyType === 'tournament_chips') ? "numeric" : "decimal"}
                                                onChange={handleInputChange}
                                                onFocus={handleInputFocus}
                                                onBlur={handleInputBlur}
                                                onKeyDown={handleKeyDown}
                                                style={{
                                                    width: '20%',
                                                    height: 'clamp(20px, 2vw, 26px)',
                                                    padding: '0px',
                                                    fontSize: isMobile ? '14px' : 'clamp(14px, 1.5vw, 16px)',
                                                    fontWeight: '600',
                                                    borderRadius: '30px',
                                                    border: 'none',
                                                    backgroundColor: '#52525D',
                                                    color: '#3DE361',
                                                    textAlign: 'center',
                                                }}
                                            />
                                        </Stack>
                                        {/* <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: isMobile ? '10px' : 'clamp(6px, 1vw, 10px)',
                                            // width: isMobile ? '78px' : 'clamp(48px, 5vw, 60px)',
                                            // height: isMobile ? ( isRaiseExpanded ? '331px' : '34px') : 'clamp(140px, 24vh, 240px)',
                                            padding: isMobile ? '7px 5px' : '',
                                            boxSizing: 'border-box',
                                            borderRadius: '4px',
                                            // position: 'fixed',
                                            // bottom: isMobile ? '58px' : controlBottom,
                                            // right: isMobile ? '10px' : 'clamp(18px, 2vw, 32px)',
                                            flexDirection: 'row',
                                            backgroundColor: isMobile ? '#292b33' : 'transparent',
                                        }}>
                                        </Box> */}
                                    </Box>
                                );
                            })()}
                        </Box>
                    )
                )}
            </Box>
        </ThemeProvider>
    );
};

export default TurnActionControls;
